import {
  isCompound,
  isOption,
  isOTCRepoSecurity,
  isSpot,
  isValidQuotePrice,
  OrdTypeEnum,
  parseDate,
  PricingModeEnum,
  QuoteStatusEnum,
  RFQTradingProtocolEnum,
  SecurityTypesUtils,
  toBig,
  toBigWithDefault,
  type QuoteMarket,
  type Security,
} from '@talos/kyoko';
import type Big from 'big.js';
import { isBefore } from 'date-fns';

/**
 * Derive the OrdType for an RFQ accept based on the security type and pricing mode.
 */
export function getOrdTypeForQuote(
  security: Security | undefined,
  pricingMode: PricingModeEnum | undefined,
  protocol: RFQTradingProtocolEnum | undefined
): OrdTypeEnum {
  if (security && isCompound(security)) {
    return OrdTypeEnum.RFQ;
  }

  if (security && isOption(security)) {
    return OrdTypeEnum.RFQ;
  }

  if (security && isOTCRepoSecurity(security)) {
    return OrdTypeEnum.RFQ;
  }

  if (
    security &&
    SecurityTypesUtils.PredictionOutcome.isProductType(security) &&
    protocol === RFQTradingProtocolEnum.Negotiated
  ) {
    return OrdTypeEnum.RFQ;
  }

  if (pricingMode === PricingModeEnum.SpreadToFixing) {
    return OrdTypeEnum.RFQ;
  }

  return OrdTypeEnum.LimitAllIn;
}

export interface QuoteMarketWithBestBidOffer extends QuoteMarket {
  isBestBid: boolean;
  isBestOffer: boolean;
}

export type RfqBestQuoteBy = 'px' | 'qty';
export type BestQuoteSide = 'bid' | 'offer';
type QuoteComparisonResult = -1 | 0 | 1;

export interface CalculateBestQuotesParams {
  quotes?: QuoteMarket[] | undefined;
  pricingMode?: PricingModeEnum | undefined;
  onlyActive?: boolean;
  bestBy?: RfqBestQuoteBy;
}

interface SortQuotesByBestParams extends CalculateBestQuotesParams {
  side: BestQuoteSide;
}

/**
 * Some quote-market responses omit BidQty/OfferQty even though they include a valid
 * BidPx/OfferPx. In that case, show the top-level Quote.OrderQty for that market price.
 */
export function getMarketQty(
  marketQty: string | null | undefined,
  orderQty: string | undefined,
  px: string | undefined
) {
  return marketQty ?? (px != null ? orderQty : undefined);
}

/**
 * Marks the best bid and offer across RFQ market quotes.
 *
 * Best quotes are selected by the requested mode first, then by the opposite
 * mode as a tie-breaker, and finally by earliest transact time. Quantity is
 * always better when higher. Price follows the trading side: higher bid is
 * better for selling, lower offer is better for buying.
 */
export const calculateBestQuotes = ({
  quotes = [],
  pricingMode = undefined,
  onlyActive = true,
  bestBy = 'px',
}: CalculateBestQuotesParams = {}): QuoteMarketWithBestBidOffer[] => {
  const activeQuotes = onlyActive ? quotes.filter(quote => quote.QuoteStatus === QuoteStatusEnum.Open) : quotes;

  let bestBid: QuoteMarket | undefined;
  let bestOffer: QuoteMarket | undefined;
  for (const quote of activeQuotes) {
    if (isQuoteRankable('bid', quote, pricingMode)) {
      if (bestBid == null) {
        bestBid = quote;
      } else {
        if (isQuoteBetterThan('bid', quote, bestBid, bestBy)) {
          bestBid = quote;
        }
      }
    }

    if (isQuoteRankable('offer', quote, pricingMode)) {
      if (bestOffer == null) {
        bestOffer = quote;
      } else {
        if (isQuoteBetterThan('offer', quote, bestOffer, bestBy)) {
          bestOffer = quote;
        }
      }
    }
  }

  return quotes.map(quote => {
    const q: QuoteMarketWithBestBidOffer = { ...quote, isBestBid: false, isBestOffer: false };
    if (quote.Market === bestBid?.Market) {
      q.isBestBid = true;
    }
    if (quote.Market === bestOffer?.Market) {
      q.isBestOffer = true;
    }
    return q;
  });
};

/**
 * Sorts RFQ market quotes from best to worst for one executable side.
 *
 * By default (onlyActive=true), this also filters out non-Open quotes and quotes without a valid executable price.
 *
 * This uses the same comparison rules as `calculateBestQuotes`: price or
 * quantity is compared first, the other value breaks ties, and earliest
 * transact time wins when economic terms match.
 */
export function sortQuotesByBest({
  quotes = [],
  pricingMode = undefined,
  onlyActive = true,
  bestBy = 'px',
  side,
}: SortQuotesByBestParams): QuoteMarket[] {
  const activeQuotes = onlyActive ? quotes.filter(quote => quote.QuoteStatus === QuoteStatusEnum.Open) : quotes;
  const rankableQuotes = activeQuotes.filter(quote => isQuoteRankable(side, quote, pricingMode));

  return rankableQuotes.toSorted((a, b) => {
    if (isQuoteBetterThan(side, a, b, bestBy)) {
      return -1;
    }

    if (isQuoteBetterThan(side, b, a, bestBy)) {
      return 1;
    }

    return 0;
  });
}

export function getRFQSyntheticCrossSymbol(security: Security | undefined): string | undefined {
  if (!security || !isSpot(security)) {
    return undefined;
  }

  return `${security.BaseCurrency}-USD`;
}

/**
 * Returns true when quote should replace bestQuote for the given side.
 *
 * The requested bestBy mode is compared first, then the other mode is used as a
 * tie-breaker, and earliest TransactTime wins when both values match.
 */
function isQuoteBetterThan(
  side: BestQuoteSide,
  quote: QuoteMarket,
  bestQuote: QuoteMarket,
  bestBy: RfqBestQuoteBy
): boolean {
  const primaryComparison = compareQuoteValue(side, quote, bestQuote, bestBy);
  if (primaryComparison !== 0) {
    return primaryComparison > 0;
  }

  // Size countering can prioritize quantity, so use the non-selected mode as the first tie-breaker.
  const tieBreakBy = bestBy === 'px' ? 'qty' : 'px';
  const tieBreakComparison = compareQuoteValue(side, quote, bestQuote, tieBreakBy);
  if (tieBreakComparison !== 0) {
    return tieBreakComparison > 0;
  }

  // Keep existing behavior stable when economic terms are equivalent.
  const quoteTransactTime = parseDate(quote.TransactTime);
  const bestQuoteTransactTime = parseDate(bestQuote.TransactTime);
  return isBefore(quoteTransactTime, bestQuoteTransactTime);
}

function isQuoteRankable(side: BestQuoteSide, quote: QuoteMarket, pricingMode: PricingModeEnum | undefined): boolean {
  const price = side === 'bid' ? quote.BidPx : quote.OfferPx;
  return isValidQuotePrice(price, quote.QuoteStatus, pricingMode);
}

/**
 * Compares either price or quantity for a bid or offer.
 *
 * Positive means quote is better than bestQuote, negative means worse, and zero
 * means equal. Quantities are better when higher; bids are better when higher;
 * offers are better when lower.
 */
function compareQuoteValue(
  side: BestQuoteSide,
  quote: QuoteMarket,
  bestQuote: QuoteMarket,
  bestBy: RfqBestQuoteBy
): QuoteComparisonResult {
  const quoteValue = getQuoteValue(side, quote, bestBy);
  const bestQuoteValue = getQuoteValue(side, bestQuote, bestBy);

  if (quoteValue == null && bestQuoteValue == null) {
    return 0;
  }

  if (quoteValue == null) {
    return -1;
  }

  if (bestQuoteValue == null) {
    return 1;
  }

  if (quoteValue.eq(bestQuoteValue)) {
    return 0;
  }

  if (bestBy === 'qty') {
    return quoteValue.gt(bestQuoteValue) ? 1 : -1;
  }

  // Bid is what we can sell into, offer is what we can buy from.
  const lowerPriceIsBetter = side === 'offer';
  const hasBetterPrice = lowerPriceIsBetter ? quoteValue.lt(bestQuoteValue) : quoteValue.gt(bestQuoteValue);
  return hasBetterPrice ? 1 : -1;
}

function getQuoteValue(side: BestQuoteSide, quote: QuoteMarket, bestBy: RfqBestQuoteBy): Big | undefined {
  if (bestBy === 'px') {
    return toBig(side === 'bid' ? quote.BidPx : quote.OfferPx);
  }

  return toBigWithDefault(side === 'bid' ? quote.BidQty : quote.OfferQty, 0);
}
